import { useEffect, useState } from "react";
import { Table, Tag, Button, Typography, Space, Tooltip, message } from "antd";
import { FolderOpenOutlined, DownloadOutlined } from "@ant-design/icons";
import { saveAs } from "file-saver";
import EstadoFinalizacionBadge from "./EstadoFinalizacionBadge";
import { descargarCarpetaInformeFinal } from "../../../services/informeTecnicoFinal/informeTecnicoFinalService";

const { Text } = Typography;

const NAVY = "#042956";

function estadoColor(estado = "") {
  const e = estado.toLowerCase();
  if (e.includes("final") || e.includes("cerrad") || e.includes("complet")) return "green";
  if (e.includes("ejecu") || e.includes("proceso") || e.includes("curso")) return "blue";
  if (e.includes("pend") || e.includes("espera")) return "gold";
  if (e.includes("suspend") || e.includes("cancel")) return "red";
  return "default";
}

function formatSize(bytes) {
  if (!bytes) return "0 MB";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(1)} ${units[i]}`;
}

/**
 * ProyectosTable
 * Props:
 *  - proyectos: mismo shape que ProyectosCards
 *  - loading: boolean
 *  - onSelect(proyecto)
 */
export default function ProyectosTable({ proyectos = [], loading = false, onSelect, onEstadoChange }) {
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [descargandoOid, setDescargandoOid] = useState(null);

  // Si cambian los filtros (nuevo array), vuelve siempre a la primera página.
  useEffect(() => {
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, [proyectos]);

  const handleDescargar = async (record) => {
    setDescargandoOid(record.oid);
    try {
      const blob = await descargarCarpetaInformeFinal(record.oid);
      saveAs(blob, `${record.codigo}_informe_tecnico_final.zip`);
    } catch (error) {
      console.error("Error al descargar la carpeta:", error);
      const status = error.response?.status;
      message.error(
        status === 404
          ? "Este proyecto todavía no tiene documentos para descargar"
          : "No se pudo descargar la carpeta"
      );
    } finally {
      setDescargandoOid(null);
    }
  };

  const columns = [
    {
      title: "Código",
      dataIndex: "codigo",
      key: "codigo",
      width: 100,
      fixed: "left",
      render: (codigo) => (
        <span
          style={{
            background: NAVY,
            color: "#ffffff",
            fontWeight: 700,
            fontSize: 12,
            padding: "3px 10px",
            borderRadius: 6
          }}
        >
          {codigo}
        </span>
      )
    },
    {
      title: "Proyecto",
      dataIndex: "proyecto",
      key: "proyecto",
      width: 260,
      render: (proyecto) => (
        <Text style={{ color: NAVY, fontWeight: 500 }} ellipsis title={proyecto}>
          {proyecto}
        </Text>
      )
    },
    {
      title: "Proyecto asociado",
      dataIndex: "Nombre_proyectoAsociado",
      key: "Nombre_proyectoAsociado",
      width: 220,
      render: (v) => (
        <Text style={{ color: "#5b6b7c" }} ellipsis title={v}>
          {v || "—"}
        </Text>
      )
    },
    {
      title: "Archivos",
      dataIndex: "archivos",
      key: "archivos",
      width: 100,
      align: "center",
      render: (v) => <Text style={{ color: "#5b6b7c" }}>{v ?? 0}</Text>
    },
    {
      title: "Carpetas",
      dataIndex: "carpetas",
      key: "carpetas",
      width: 100,
      align: "center",
      render: (v) => <Text style={{ color: "#5b6b7c" }}>{v ?? 0}</Text>
    },
    {
      title: "Peso",
      dataIndex: "pesoBytes",
      key: "pesoBytes",
      width: 110,
      align: "center",
      render: (v) => <Text style={{ color: "#5b6b7c" }}>{formatSize(v)}</Text>
    },
    {
      title: "Estado",
      key: "estado",
      width: 260,
      render: (_, record) => (
        <Space size={8}>
          <Tag color={estadoColor(record.ac_estado)} style={{ borderRadius: 6, fontWeight: 500 }}>
            {record.ac_estado || "Sin estado"}
          </Tag>
          <EstadoFinalizacionBadge proyecto={record} onChange={onEstadoChange} />
        </Space>
      )
    },
    {
      title: "Acción",
      key: "accion",
      width: 210,
      fixed: "right",
      render: (_, record) => (
        <Space size={6}>
          <Button
            type="primary"
            size="small"
            icon={<FolderOpenOutlined />}
            onClick={() => onSelect?.(record)}
            style={{ background: NAVY, borderRadius: 8, fontWeight: 500 }}
          >
            Ver documentos
          </Button>
          <Tooltip title="Descargar la carpeta comprimida (ZIP)">
            <Button
              size="small"
              icon={<DownloadOutlined />}
              loading={descargandoOid === record.oid}
              onClick={() => handleDescargar(record)}
              style={{ borderRadius: 8, color: NAVY, borderColor: "#d7dee7" }}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  return (
    <div
      style={{
        background: "#ffffff",
        borderRadius: 16,
        padding: 8,
        border: "1px solid #eef1f5",
        boxShadow: "0 1px 4px rgba(4, 41, 86, 0.06)"
      }}
    >
      <Table
        rowKey="oid"
        loading={loading}
        columns={columns}
        dataSource={proyectos}
        scroll={{ x: 1200 }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: proyectos.length,
          showSizeChanger: true,
          pageSizeOptions: ["10", "25", "50", "100"],
          showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} proyectos`,
          onChange: (current, pageSize) => setPagination({ current, pageSize })
        }}
      />
    </div>
  );
}
